/**
 * White paper behind a cover render — and *only* behind the paper.
 *
 * ── The problem ──────────────────────────────────────────────────────────
 * A cover thumbnail has two visually distinct regions that a naive
 * `background-color` cannot tell apart:
 *
 *   1. The artwork itself — the rendered PDF page. This is paper. Paper is
 *      white, so a page whose PDF has no opaque background must still read as
 *      white, exactly as it will come off the press.
 *   2. The filler around it — the letterbox bands `object-fit: contain` adds
 *      so that every tile in the grid is the same size. This is *not* paper.
 *      It is the panel, and it must stay panel-coloured, otherwise a cover
 *      that was not built to its trim hides its own white bands against a
 *      white backdrop and the thumbnail flatters a cover that will
 *      disappoint at press. That is the whole point of the grey backdrop in
 *      the thumbnail CSS.
 *
 * CSS cannot express that split on its own. `background` on a replaced
 * element paints the entire padding box, so `background: white` colours the
 * letterbox bands too, and `background: transparent` lets the grey show
 * through a transparent PDF. There is no `background-size` keyword that means
 * "however big the contained image ended up", because the browser does not
 * expose the fitted rectangle to CSS.
 *
 * ── The fix ──────────────────────────────────────────────────────────────
 * Compute the fitted rectangle in script — it is the same arithmetic the
 * browser already did for `object-fit` — and paint a white *gradient layer*
 * at exactly that size, centred to match the default `object-position`. The
 * background-colour stays transparent, so the bands keep showing the panel.
 *
 * A gradient rather than a colour because `background-size` only has meaning
 * for a background *image*; `linear-gradient(#fff, #fff)` is the cheapest
 * solid-white image available and needs no network round trip.
 *
 * ── Why this is not done on the server ───────────────────────────────────
 * It arguably should be, and if the render pipeline ever starts flattening
 * pages onto white this helper becomes a harmless no-op. But the `src` here
 * is a signed URL to an already-rendered PNG in S3, so the browser cannot
 * re-composite it: drawing a cross-origin image to a canvas taints the
 * canvas and `toDataURL` then throws.
 */

/** The `object-fit` values under which the image does not fill its whole box. */
const FITTED = ['contain', 'scale-down', 'none'];

/**
 * Size a white backing layer to the artwork inside `img`.
 *
 * Safe to call at any time and as often as you like: until the image has both
 * loaded (so it has a natural size) and been laid out (so it has a box), there
 * is no rectangle to compute and the plate is simply cleared — painting one
 * early would put a white rectangle over empty space.
 */
export function paintCoverWhitePlate(img: HTMLImageElement): void {
  if (!img || !img.style) {
    return;
  }

  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  const boxWidth = img.clientWidth;
  const boxHeight = img.clientHeight;

  if (!naturalWidth || !naturalHeight || !boxWidth || !boxHeight) {
    clearCoverWhitePlate(img);
    return;
  }

  let plateWidth = boxWidth;
  let plateHeight = boxHeight;

  const objectFit = currentObjectFit(img);
  if (FITTED.indexOf(objectFit) !== -1) {
    // `none` renders at natural size; the others scale to fit. `scale-down`
    // is `contain` that is not allowed to enlarge.
    let scale = objectFit === 'none'
      ? 1
      : Math.min(boxWidth / naturalWidth, boxHeight / naturalHeight);
    if (objectFit === 'scale-down') {
      scale = Math.min(scale, 1);
    }
    // Clipped rather than letterboxed on an axis that overflows: the plate
    // must never be larger than the box, or white would spill into the bands.
    plateWidth = Math.min(naturalWidth * scale, boxWidth);
    plateHeight = Math.min(naturalHeight * scale, boxHeight);
  }
  // `fill` and `cover` both cover the box completely, so the plate is the box.

  // Round up. A fractional plate can leave a hairline of panel grey down the
  // edge of the artwork, which reads as a rendering fault; up to a pixel of
  // white bleeding into the band does not.
  img.style.backgroundColor = 'transparent';
  img.style.backgroundImage = 'linear-gradient(#ffffff, #ffffff)';
  img.style.backgroundRepeat = 'no-repeat';
  img.style.backgroundPosition = 'center';
  img.style.backgroundSize = Math.ceil(plateWidth) + 'px ' + Math.ceil(plateHeight) + 'px';
}

/** Remove the plate, leaving whatever the stylesheet says behind the element. */
export function clearCoverWhitePlate(img: HTMLImageElement): void {
  if (!img || !img.style) {
    return;
  }
  img.style.backgroundImage = 'none';
}

/**
 * `object-fit` as the browser actually resolved it.
 *
 * Read from the computed style rather than assumed, because the two callers
 * disagree — the Book cover uses `contain` and the Draft cover uses `cover` —
 * and a helper that guessed would silently paint the wrong rectangle for one
 * of them.
 */
function currentObjectFit(img: HTMLImageElement): string {
  const view = img.ownerDocument && img.ownerDocument.defaultView;
  if (!view || !view.getComputedStyle) {
    return 'fill';
  }
  const computed: any = view.getComputedStyle(img);
  return ((computed && computed.objectFit) || 'fill').trim();
}

